/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ActionsApiConfiguration } from '../actions-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Action } from '../models/action';

@Injectable({
  providedIn: 'root',
})
export class ActionsService extends BaseService {
  constructor(config: ActionsApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation GetActions
   */
  static readonly GetActionsPath = '/actions';

  /**
   * Note that this is not the full list of actions that could be visible on the board - just those owned by the board.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `GetActions()` instead.
   *
   * This method doesn't expect any response body
   */
  GetActions$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;
  }): Observable<StrictHttpResponse<Array<Action>>> {
    const rb = new RequestBuilder(this.rootUrl, '/actions', 'get');
    if (params) {
      rb.query('organisationId', params.organisationId);
      rb.query('boardId', params.boardId);
    }
    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<Action>>;
        })
      );
  }

  /**
   * Note that this is not the full list of actions that could be visible on the board - just those owned by the board.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `GetActions$Response()` instead.
   *
   * This method doesn't expect any response body
   */
  GetActions(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;
  }): Observable<{ [key: string]: Action }> {
    return this.GetActions$Response(params).pipe(map((r: StrictHttpResponse<Array<Action>>) => r.body as any));
  }

  /**
   * Path part for operation AddAction
   */
  static readonly AddActionPath = '/actions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `AddAction()` instead.
   *
   * This method sends `application/json` and handles response body of type `application/json`
   */
  AddAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The return type of the object. Either 'object' or omitted
     */
    returnType: string;
    /**
     * Kanban area to add to the board
     */
    body: Action;
  }): Observable<StrictHttpResponse<Action>> {
    const rb = new RequestBuilder(this.rootUrl, '/actions', 'post');
    if (params) {
      rb.query('organisationId', params.organisationId);
      rb.query('boardId', params.boardId);
      rb.query('returnType', params.returnType);
      rb.body(params.body, 'application/json');
    }
    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Action>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `AddAction$Response()` instead.
   *
   * This method sends `application/json` and handles response body of type `application/json`
   */
  AddAction(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The return type of the object. Either 'object' or omitted
     */
    returnType: string;
    /**
     * Kanban area to add to the board
     */
    body: Action;
  }): Observable<Action> {
    return this.AddAction$Response(params).pipe(map((r: StrictHttpResponse<Action>) => r.body as Action));
  }

  /**
   * Path part for operation GetAction
   */
  static readonly GetActionPath = '/actions/{actionId}';

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `GetAction()` instead.
   *
   * This method doesn't expect any response body
   */
  GetAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;
  }): Observable<StrictHttpResponse<Action>> {
    const rb = new RequestBuilder(this.rootUrl, '/actions/{actionId}', 'get');
    if (params) {
      rb.query('organisationId', params.organisationId);
      rb.query('boardId', params.boardId);
      rb.path('actionId', params.actionId);
      rb.query('_etag', params.etag);
    }
    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Action>;
        })
      );
  }

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `GetAction$Response()` instead.
   *
   * This method doesn't expect any response body
   */
  GetAction(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;
  }): Observable<Action> {
    return this.GetAction$Response(params).pipe(map((r: StrictHttpResponse<Action>) => r.body as Action));
  }

  /**
   * Path part for operation DeleteAction
   */
  static readonly DeleteActionPath = '/actions/{actionId}';

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `DeleteAction()` instead.
   *
   * This method doesn't expect any response body
   */
  DeleteAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;
  }): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, '/actions/{actionId}', 'delete');
    if (params) {
      rb.query('organisationId', params.organisationId);
      rb.query('boardId', params.boardId);
      rb.path('actionId', params.actionId);
      rb.query('_etag', params.etag);
    }
    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `DeleteAction$Response()` instead.
   *
   * This method doesn't expect any response body
   */
  DeleteAction(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;
  }): Observable<void> {
    return this.DeleteAction$Response(params).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation UpdateAction
   */
  static readonly UpdateActionPath = '/actions/{actionId}';

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `UpdateAction()` instead.
   *
   * This method sends `application/json` and handles response body of type `application/json`
   */
  UpdateAction$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;

    /**
     * Action attributes to update
     */
    body: Action;
  }): Observable<StrictHttpResponse<Action>> {
    const rb = new RequestBuilder(this.rootUrl, '/actions/{actionId}', 'patch');
    if (params) {
      rb.query('organisationId', params.organisationId);
      rb.query('boardId', params.boardId);
      rb.path('actionId', params.actionId);
      rb.query('_etag', params.etag);
      rb.body(params.body, 'application/json');
    }
    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Action>;
        })
      );
  }

  /**
   * Optional extended description in CommonMark or HTML.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `UpdateAction$Response()` instead.
   *
   * This method sends `application/json` and handles response body of type `application/json`
   */
  UpdateAction(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;

    /**
     * Board the user is acting under for this operation.
     */
    boardId: string;

    /**
     * The Action UUID
     */
    actionId: string;

    /**
     * _etag of the object of this operation.
     */
    etag?: string;

    /**
     * Action attributes to update
     */
    body: Action;
  }): Observable<Action> {
    return this.UpdateAction$Response(params).pipe(map((r: StrictHttpResponse<Action>) => r.body as Action));
  }
}
